import { useRef } from 'react';

const steps = [
  {
    number: '01',
    title: 'Pull a Bag from the Roll',
    desc: 'The built-in bag roll dispenser means you\'re always ready. One quick tear and you\'ve got a fresh bag in hand before your dog even finishes.',
    image: 'https://storage.readdy-site.link/project_files/1b4b5470-43ee-4e4c-a03f-80bf782ac886/184df24a-e139-4d79-9f0f-e48b2058d52c_IMG_2312.png?v=e7903f58e14f46f03c7b245beae14e9f',
  },
  {
    number: '02',
    title: 'Clamp Open & Sleeve the Bag',
    desc: 'Open the claw wide and slip the bag over the outside of the jaws. It locks in place instantly — no fumbling, no inside-out bag wrestling.',
    image: 'https://storage.readdy-site.link/project_files/1b4b5470-43ee-4e4c-a03f-80bf782ac886/1fa2fc7a-c8fd-4999-804e-092f8a70c3d4_IMG_2311-2.png?v=00f6e2a6eeb31fc050a356d119514c93',
  },
  {
    number: '03',
    title: 'Scoop, Seal & Walk On',
    desc: 'Lower, squeeze, done. The claw picks it all up in one clean motion. Release into the bag, tie it off, and keep walking — hands never touched a thing.',
    image: 'https://storage.readdy-site.link/project_files/1b4b5470-43ee-4e4c-a03f-80bf782ac886/e90697bf-51bd-4d1c-a89f-bf9b048229e5_IMG_2313.png?v=6491bcc35a784eef2ee6f2b7d3183ec2',
  },
];

export default function ProductDemo() {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoEnded = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  return (
    <section id="how-it-works" className="py-14 md:py-32 bg-[#6B7A66]">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 md:mb-16">
          <span className="text-white/60 text-xs font-bold uppercase tracking-widest">How It Works</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mt-3 mb-3 md:mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Three Steps to Clean Walks
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-xl mx-auto">
            It really is this simple. No learning curve, no fuss — just clean.
          </p>
        </div>

        {/* Video Demo */}
        <div className="mb-10 md:mb-16 flex flex-col items-center">
          <div className="relative mx-auto w-full max-w-xs aspect-[9/16] rounded-2xl overflow-hidden border-2 border-white/20 bg-black">
            <video
              ref={videoRef}
              src="https://storage.readdy-site.link/project_files/1b4b5470-43ee-4e4c-a03f-80bf782ac886/cd6daa57-5472-4836-aea9-09b6d47a08a4_ScreenRecording_04-03-2026-19-35-49_1.mp4?v=eb19e3f12912985160fd72e08ae15f58"
              autoPlay
              muted
              loop
              playsInline
              onEnded={handleVideoEnded}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <p className="text-white/50 text-sm text-center mt-4">Watch how easy it is to use</p>
        </div>

        {/* Mobile compact steps */}
        <div className="md:hidden flex flex-col gap-3 mb-8">
          {steps.map((step) => (
            <div key={step.number} className="flex items-center gap-4 bg-white/10 rounded-2xl px-5 py-4">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-full flex-shrink-0">
                <span className="text-[#C4956A] font-bold text-sm">{step.number}</span>
              </div>
              <div>
                <p className="text-white font-semibold text-sm">{step.title}</p>
                <p className="text-white/65 text-xs mt-0.5 leading-snug">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop full grid */}
        <div className="hidden md:grid grid-cols-3 gap-8">
          {steps.map((step) => (
            <div key={step.number} className="flex flex-col items-center text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-full mb-6 flex-shrink-0">
                <span className="text-[#C4956A] font-bold text-lg">{step.number}</span>
              </div>
              <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
              <div className="w-full aspect-[3/4] rounded-2xl overflow-hidden border-2 border-white/20 mb-6">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-top"
                  style={{ objectPosition: 'top center' }}
                />
              </div>
              <p className="text-white/70 text-sm leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 md:mt-16 text-center">
          <a
            href="#product"
            className="inline-flex items-center gap-2 bg-[#C4956A] hover:bg-[#b5845a] text-white font-bold px-8 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer text-base"
          >
            Try It Risk-Free — $18
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-white/50 text-sm mt-3">30-day money-back guarantee</p>
        </div>
      </div>
    </section>
  );
}
